import {
  CourseClassCalendarDto,
  CreateMsEventDto,
  MsEventDto,
  MsOfferingCalendarDto,
  MsPrivateCalendarDto,
  ProgramManagementCalendar,
} from "@clients/calendarHubClient"
import { TermDto } from "@clients/termClient"
import {
  CourseDescriptorDto,
  ProgramDto,
  TrackDto,
} from "@masterschool/course-builder-api"
import { v4 as uuidv4 } from "uuid"
import {
  CalendarFilter,
  CalendarFilters,
  CalendarGroupings,
  CalendarLinkEntity,
  DoubleNestedCalendarGroupingSubgroup,
  MsCalendarPrivateCalendarProps,
  MsCalendarProps,
  MsEventProps,
  MsOfferingCalendarProps,
  MuiColorPalette,
} from "@masterschool/ui-calendar/dist/src/types"
import { legacyProgramDisplayName } from "../../programs/program/ui/program+ui"
import { courseClassDisplayName } from "@utils/courseInstanceUtils"
import { formatDateAsYearAndLongMonth } from "@utils/date"

/*
The file is still WIP,
 we don't have yet the full API, so some values are hardcoded here just to make the app work
*/

export const mapToMsEventDto = (event: MsEventProps): CreateMsEventDto => {
  if (event.type === "unassigned") {
    console.error(`Unsupported event type: ${event.type}`)
    throw new Error(`Unsupported event type: ${event.type}`)
  }
  return {
    title: event.title,
    description: event.description || "",
    start: event.start,
    end: event.end,
    presentAsAllDay: event.allDay || false,
    type: event.type,
    participants: event.participantCalendarIds || [],
    rrule: event.recurringRule || undefined,
    rruleUntil: event.recurringUntil || undefined,
    rruleTzid: event.recurringRuleTzid || undefined,
    hasVc: event.vc?.hasVc || false,
    instructorCalendarId:
      event.type === "live-session" ? event.instructorCalendarId : undefined,
    calendarId: event.calendarId,
    recordingLinks: event.recordingLinks || [],
    // Opt-in recording for non-live-session events (the checkbox is hidden for
    // live sessions, which the backend always records).
    shouldRecord: event.shouldRecord,
    // Required by the campus create endpoint; a fresh key per save so each
    // create makes one event (re-runs/retries no-op instead of duplicating).
    idempotencyKey: uuidv4(),
  }
}

export const mapToMsEventProps = (event: MsEventDto): MsEventProps => {
  return {
    id: event.id,
    title: event.title,
    description: event.description,
    start: new Date(event.start),
    end: new Date(event.end),
    type: event.type,
    allDay: event.presentAsAllDay,
    recurringId: event.recurringGroupID,
    organizerClientId: event.organizerClientId,
    recurringRule: event.rrule || "",
    recurringUntil: event.rruleUntil ? new Date(event.rruleUntil) : undefined,
    recurringRuleTzid: event.rruleTzid || undefined,
    participantCalendarIds: event.participants,
    calendarId: event.calendarId,
    color: event.color,
    vc: event.hasVc
      ? { hasVc: event.hasVc, url: event.vcUrl || undefined }
      : undefined,
    instructorCalendarId: event.instructorCalendarId,
    recordingLinks: event.recordingLinks,
    shouldRecord: event.shouldRecord,
    organizerName: event.organizerName,
    recordingSkipReason: event.recordingSkipReason,
    mergedTitles: event.mergedTitles,
    mergedEvents: event.mergedEvents,
    calendarMemberships: event.calendarMemberships,
  } as MsEventProps // TO DO check those typescript errors
}

// Data needed to nest a program's classes under course groups ("Study halls",
// then one group per course instance). Courses + terms are app-wide redux data
// (fetchAppData), so both the main and the program calendar can supply it.
export type CourseGroupingData = {
  courseDescriptors: CourseDescriptorDto[]
  terms: TermDto[]
}

export const buildMsCalendarGroupingsAndFiltersForProgramOwner = (
  calendars: ProgramManagementCalendar,
  programs: (ProgramDto & { track?: TrackDto | undefined })[],
  // When provided, each program sub-group becomes lazy: its class calendars are
  // fetched the first time it's expanded (onFirstExpand) and merged in alongside
  // the offering calendar, with a spinner while loading.
  lazyClasses?: {
    classCalendarsByProgram: Record<
      string,
      { isLoading: boolean; calendars: CourseClassCalendarDto[] }
    >
    loadProgramClasses: (programId: string) => void
  },
  // When provided, each program node nests its calendars one level deeper:
  // "Study halls" (the offering calendars) plus one group per course instance.
  // Absent, classes sit flat under the program (the pre-grouping shape).
  courseGrouping?: CourseGroupingData,
): {
  groupings: CalendarGroupings
  customFilters: CalendarFilters
} => {
  const programToCalendars = calendars.programSchoolCalendars.reduce(
    (acc, calendar) => {
      const program = programs.find(
        (p) => p.id === calendar.offeringDetails.programId,
      )
      if (!program) {
        return acc
      }
      if (!acc[program.id]) {
        acc[program.id] = {
          // domain+date alone collides across same-day cohorts (e.g. four
          // "Software May 12, 2026"); add the track so each program header is
          // distinct. Language is intentionally NOT shown here — a program spans
          // both EN and DE classes, so it lives on the class leaves instead
          // (courseClassDisplayName), where it's accurate per class.
          title: [
            legacyProgramDisplayName(program),
            program.track?.externalName || program.track?.internalName,
          ]
            .filter(Boolean)
            .join(" · "),
          calendars: [],
          // Just-in-time navigation: the program node opens its LMS program
          // page. programId is a ProgramDto.id — exactly what /program/:programId
          // resolves — so the shortcut always lands on a real page.
          linkEntity: {
            type: "program",
            programId: program.id,
          } satisfies CalendarLinkEntity,
        }
      }
      acc[program.id].calendars.push(mapToMsCalendarSchoolProps(calendar))
      return acc
    },
    {} as Record<string, DoubleNestedCalendarGroupingSubgroup>,
  )

  // Most-recent programs first (by start date desc), so current cohorts are at
  // the top of the menu instead of in arbitrary backend order.
  const programStartTime = (programId: string) => {
    const start = programs.find((p) => p.id === programId)?.startDate
    return start ? new Date(start).getTime() : 0
  }

  // When lazy class-loading is enabled, each program sub-group fetches its class
  // calendars on first expand and merges them next to the offering calendar.
  const programSubGroups: DoubleNestedCalendarGroupingSubgroup[] =
    Object.entries(programToCalendars)
      .sort(([aId], [bId]) => programStartTime(bId) - programStartTime(aId))
      .map(([programId, subGroup]) => {
        if (!lazyClasses) {
          return subGroup
        }
        const offeringCalendars = subGroup.calendars
        const classState = lazyClasses.classCalendarsByProgram[programId]
        const classDtos = classState?.calendars ?? []
        const lazyProps = {
          onFirstExpand: () => lazyClasses.loadProgramClasses(programId),
          isLoading: classState?.isLoading ?? false,
        }
        if (courseGrouping) {
          // Nested shape: the program node holds child groups, not flat rows.
          // Its checkbox still selects the whole subtree (the menu flattens
          // recursively), so "tick the program" behaves identically.
          return {
            title: subGroup.title,
            calendars: [],
            // Carry the program link onto the nested shape too (the map builds a
            // fresh node here rather than spreading subGroup).
            linkEntity: subGroup.linkEntity,
            subGroups: [
              ...(offeringCalendars.length > 0
                ? [{ title: "Study halls", calendars: offeringCalendars }]
                : []),
              ...groupClassCalendarsByCourse(
                classDtos,
                courseGrouping.courseDescriptors,
                courseGrouping.terms,
              ),
            ],
            ...lazyProps,
          }
        }
        return {
          ...subGroup,
          calendars: [
            ...offeringCalendars,
            ...classDtos.map((c) => mapToMsCalendarProps(c)),
          ],
          ...lazyProps,
        }
      })

  const groupings: CalendarGroupings = [
    {
      type: "double-nested",
      groupingsType: "classes",
      groupingsAction: "all",
      // Both levels are selectable: the group selects all programs, each
      // sub-group selects one whole program, each leaf one class.
      title: "Programs / Classes",
      subGroups: programSubGroups,
    },
    {
      title: "Program Staff",
      type: "single",
      groupingsType: "staff",
      groupingsAction: "all",
      calendars: calendars.programStaffCalendars.map((c) =>
        mapToMsPersonalCalendarProps(c),
      ),
    },
    {
      title: "Students",
      type: "single",
      groupingsType: "students",
      groupingsAction: "all",
      calendars: calendars.studentCalendars.map((c) =>
        mapToMsPersonalCalendarProps(c, ["student-name"]),
      ),
    },
    {
      title: "Masterschool Staff",
      type: "single",
      groupingsType: "staff",
      groupingsAction: "invite",
      calendars: calendars.allMasterschoolStaffCalendars.map((c) =>
        mapToMsPersonalCalendarProps(c, ["staff-name"]),
      ),
    },
  ]

  return {
    groupings,
    customFilters: {
      filters: getFilters(Boolean(courseGrouping)),
      relations: "OR",
    },
  }
}

// One sub-group per course instance (most-recent term first), each holding its
// class calendars tagged with course/term/language for the custom filters.
export function groupClassCalendarsByCourse(
  classCalendars: CourseClassCalendarDto[],
  courseDescriptors: CourseDescriptorDto[],
  terms: TermDto[],
): DoubleNestedCalendarGroupingSubgroup[] {
  const courseInstanceToClassCalendars = classCalendars.reduce(
    (acc, calendar) => {
      const courseInstanceId = calendar.courseClassDetails.courseInstanceId
      if (!acc[courseInstanceId]) {
        acc[courseInstanceId] = {
          calendars: [],
          course: courseDescriptors.find(
            (cd) => cd.id === calendar.courseClassDetails.courseId,
          ),
          term: terms.find((t) => t.id === calendar.courseClassDetails.termId),
        }
      }
      acc[courseInstanceId].calendars.push(calendar)
      return acc
    },
    {} as Record<
      string,
      {
        calendars: CourseClassCalendarDto[]
        course: CourseDescriptorDto | undefined
        term: TermDto | undefined
      }
    >,
  )

  return Object.keys(courseInstanceToClassCalendars)
    .sort((a, b) => {
      // Most-recent term first, so current courses sit at the top.
      const termA = courseInstanceToClassCalendars[a].term?.startDate
      const termB = courseInstanceToClassCalendars[b].term?.startDate
      return new Date(termB ?? 0).getTime() - new Date(termA ?? 0).getTime()
    })
    .msCompactMap((courseInstanceId) => {
      const { calendars, course, term } =
        courseInstanceToClassCalendars[courseInstanceId]
      return {
        title: course?.name ?? "Unknown course",
        // Just-in-time navigation: the course node opens its LMS course page.
        // course.id is a CourseDescriptorDto id — exactly what the
        // /course-manager/course/:courseId route resolves (it redirects to the
        // course's current instance) — so the shortcut lands on a real page.
        // Omitted when the descriptor is missing: an "Unknown course" header has
        // nothing to link to.
        ...(course
          ? {
              linkEntity: {
                type: "course",
                courseId: course.id,
              } satisfies CalendarLinkEntity,
            }
          : {}),
        calendars: calendars.map((c) => ({
          ...mapToMsCalendarProps(c),
          tags: getCourseClassCalendarTags(c, course, term),
        })),
      }
    })
}

const getCourseClassCalendarTags = (
  calendar: CourseClassCalendarDto,
  course: CourseDescriptorDto | undefined,
  term: TermDto | undefined,
) => {
  const tags: MsCalendarProps["tags"] = {}

  // Only tag a language when there actually is one — cohort/study-hall calendars
  // have none, and a [null]/[''] tag would render a blank badge and mis-filter.
  if (calendar.courseClassDetails.language) {
    tags.language = [calendar.courseClassDetails.language]
  }

  if (course) {
    tags["course"] = [course.name]
  }
  if (term) {
    tags["term"] = [formatDateAsYearAndLongMonth(term.startDate)]
  }

  return tags
}

const mapToMsCalendarSchoolProps = (
  calendar: MsOfferingCalendarDto,
): MsOfferingCalendarProps => {
  return {
    id: calendar.id,
    title: calendar.offeringDetails.name,
    color: calendar.color as MuiColorPalette,
    type: "offering",
    offeringClientId: calendar.offeringDetails.offeringClientId,
  }
}

const mapToMsPersonalCalendarProps = (
  calendar: MsPrivateCalendarDto,
  filterFields: string[] = [],
): MsCalendarPrivateCalendarProps => {
  return {
    id: calendar.id,
    title: calendar.name,
    color: calendar.color as MuiColorPalette,
    type: "private",
    userClientId: calendar.userClientId,
    roles: calendar.roles || [],
    tags: getCalendarTags(calendar, filterFields),
  }
}

const mapToCourseClassCalendarProps = (
  calendar: CourseClassCalendarDto,
): MsOfferingCalendarProps => {
  return {
    id: calendar.id,
    title: courseClassDisplayName(
      calendar.courseClassDetails.courseClassName,
      calendar.courseClassDetails.language,
    ),
    color: calendar.color as MuiColorPalette,
    type: "offering",
    offeringClientId: calendar.courseClassDetails.courseClassId,
  }
}

export const mapToMsCalendarProps = (
  calendar:
    | MsPrivateCalendarDto
    | MsOfferingCalendarDto
    | CourseClassCalendarDto,
): MsCalendarPrivateCalendarProps | MsOfferingCalendarProps => {
  if ("offeringDetails" in calendar) {
    return mapToMsCalendarSchoolProps(calendar)
  } else if ("courseClassDetails" in calendar) {
    return mapToCourseClassCalendarProps(calendar)
  } else {
    return mapToMsPersonalCalendarProps(calendar)
  }
}

const getCalendarTags = (calendar: MsPrivateCalendarDto, fields: string[]) => {
  const tags: MsCalendarProps["tags"] = {}

  fields.forEach((field) => {
    switch (field) {
      case "student-name":
      case "staff-name":
        tags[field] = [calendar.name]
        break
      default:
        break
    }
  })

  return tags
}

const getFilters = (includeClassFacets: boolean): CalendarFilter[] => [
  {
    field: "student-name",
    isMultiSelection: true,
    isRequired: false,
    label: "Student Name",
    operators: ["equals"],
    type: "string",
  },
  {
    field: "staff-name",
    isMultiSelection: true,
    isRequired: false,
    label: "Staff Name",
    operators: ["equals"],
    type: "string",
  },
  // Course-grouped class leaves carry term/course/language tags, so those
  // facets only exist when course grouping is active.
  ...(includeClassFacets
    ? ([
        {
          field: "term",
          isMultiSelection: true,
          isRequired: false,
          label: "Term",
          operators: ["equals"],
          type: "string",
        },
        {
          field: "course",
          isMultiSelection: true,
          isRequired: false,
          label: "Course",
          operators: ["equals"],
          type: "string",
        },
        {
          field: "language",
          isMultiSelection: true,
          isRequired: false,
          label: "Language",
          operators: ["equals"],
          type: "string",
        },
      ] as CalendarFilter[])
    : []),
]

// Menu groupings for the logged-in instructor's own calendars, prepended to the
// program-owner menu so an instructor gets selectable checkboxes for their
// personal calendar (where their sessions live) and any classes they staff.
export const buildInstructorGroupings = (instructorCalendars: {
  personalCalendar?: MsPrivateCalendarDto
  schoolCalendars: MsOfferingCalendarDto[]
}): CalendarGroupings => {
  const groupings: CalendarGroupings = []
  if (instructorCalendars.personalCalendar) {
    groupings.push({
      title: "My calendar",
      type: "single",
      groupingsType: "staff",
      groupingsAction: "all",
      calendars: [
        mapToMsPersonalCalendarProps(instructorCalendars.personalCalendar),
      ],
    })
  }
  if (instructorCalendars.schoolCalendars.length > 0) {
    groupings.push({
      title: "My classes",
      type: "single",
      groupingsType: "classes",
      groupingsAction: "all",
      calendars: instructorCalendars.schoolCalendars.map((c) =>
        mapToMsCalendarSchoolProps(c),
      ),
    })
  }
  return groupings
}

// Menu groupings for a mentor's / career-success advisor's scope — the same
// sets the campus staff calendar shows them, so the same person sees the same
// sidebar on both apps. excludeCalendarIds skips calendars another grouping
// already lists (own calendar via the instructor grouping, shared schools),
// so mixed-role users don't get duplicate rows.
export const buildMentorAndCsaGroupings = (
  mentorCalendars: {
    personalCalendar?: MsPrivateCalendarDto
    studentCalendars: MsPrivateCalendarDto[]
    schoolCalendars: MsOfferingCalendarDto[]
  },
  csaCalendars: {
    personalCalendar?: MsPrivateCalendarDto
    studentCalendars: MsPrivateCalendarDto[]
  },
  excludeCalendarIds: Set<string>,
): CalendarGroupings => {
  const groupings: CalendarGroupings = []
  const personalCalendar =
    mentorCalendars.personalCalendar ?? csaCalendars.personalCalendar
  if (personalCalendar && !excludeCalendarIds.has(personalCalendar.id)) {
    groupings.push({
      title: "My calendar",
      type: "single",
      groupingsType: "staff",
      groupingsAction: "all",
      calendars: [mapToMsPersonalCalendarProps(personalCalendar)],
    })
  }
  const studentById = new Map<string, MsPrivateCalendarDto>()
  const allStudentCalendars = [
    ...mentorCalendars.studentCalendars,
    ...csaCalendars.studentCalendars,
  ]
  allStudentCalendars.forEach((calendar) => {
    if (!excludeCalendarIds.has(calendar.id)) {
      studentById.set(calendar.id, calendar)
    }
  })
  if (studentById.size > 0) {
    groupings.push({
      title: "My students",
      type: "single",
      groupingsType: "students",
      groupingsAction: "all",
      calendars: Array.from(studentById.values()).map((c) =>
        mapToMsPersonalCalendarProps(c, ["student-name"]),
      ),
    })
  }
  const schoolCalendars = mentorCalendars.schoolCalendars.filter(
    (calendar) => !excludeCalendarIds.has(calendar.id),
  )
  if (schoolCalendars.length > 0) {
    groupings.push({
      title: "My schools",
      type: "single",
      groupingsType: "classes",
      groupingsAction: "all",
      calendars: schoolCalendars.map((c) => mapToMsCalendarSchoolProps(c)),
    })
  }
  return groupings
}
